import { useEffect, useState } from "react";
import type { WorkItem } from "../lib/types";

// SLA countdown for the ticket detail view. Ticks every second. Renders remaining time to
// sla_due_at, flips to a BREACHED state (red — the only true alarm colour in the system) once
// past due or once the server stamped sla_breached_at. Never colour-only: the state always
// carries a text word ("SLA", "Due in", "BREACHED") and an ARIA live region announces changes.
function fmt(ms: number): string {
  const abs = Math.abs(ms);
  const h = Math.floor(abs / 3_600_000);
  const m = Math.floor((abs % 3_600_000) / 60_000);
  const s = Math.floor((abs % 60_000) / 1000);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

export function SlaCountdown({ item }: { item: WorkItem }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);

  if (!item.sla_due_at && !item.sla_breached_at) {
    return (
      <span className="pill pill-neutral" role="status">
        No SLA
      </span>
    );
  }

  const breached = Boolean(item.sla_breached_at) || (item.sla_due_at ? new Date(item.sla_due_at).getTime() < now : false);
  const due = item.sla_due_at ? new Date(item.sla_due_at).getTime() : now;
  const remaining = due - now;

  if (breached) {
    return (
      <span className="pill pill-red" role="status" aria-live="polite">
        ⚑ SLA BREACHED{item.sla_due_at ? ` — ${fmt(remaining)} over` : ""}
      </span>
    );
  }
  // Warn (gold) when under an hour; otherwise brown "parked" tone.
  const urgent = remaining < 3_600_000;
  return (
    <span className={`pill ${urgent ? "pill-gold" : "pill-brown"}`} role="status" aria-live="polite">
      {urgent ? "⏳ " : ""}SLA — due in {fmt(remaining)}
    </span>
  );
}
